import Big from 'big.js';
import { NUMERIC_CONFIG } from '../config';

const toBigValue = (
  value: string | number | null | undefined
): string | number => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    return Number.isFinite(Number(value)) ? value : 0;
  }

  return 0;
};

const splitNumber = (value: string | number) => {
  const str = new Big(toBigValue(value)).toFixed();

  if (!str.includes(NUMERIC_CONFIG.decimal.internal)) {
    return { int: str, frac: '' };
  }

  const [int, frac] = str.split(NUMERIC_CONFIG.decimal.internal);
  return { int, frac };
};

const groupThousands = (
  int: string,
  sep = NUMERIC_CONFIG.thousandSeparator
) => {
  return int.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
};

const ensureMinFraction = (frac: string) => {
  return frac === '' ? '0' : frac;
};

// Display formats

export const formatReceivedPrice = (value: string | number): string => {
  const { int, frac } = splitNumber(value);

  const formattedInt = groupThousands(int);

  const finalFrac = ensureMinFraction(frac);

  return `${formattedInt}${NUMERIC_CONFIG.decimal.display}${finalFrac}`;
};

export const formatReceivedAmount = formatReceivedPrice;

// Calculated Amounts
export const applyAmountPrecision = (
  value: number,
  precision: number,
  type: 'credit' | 'debit'
): number => {
  const big = new Big(toBigValue(value));

  if (type === 'credit') {
    return big.round(precision, Big.roundDown).toNumber();
  }

  return big.round(precision, Big.roundUp).toNumber();
};

// Calculated Rates
export const applyPricePrecision = (
  value: number,
  precision: number
): number => {
  const factor = Math.pow(10, precision);

  const safeValue = Number(value.toFixed(precision + 2));

  return Math.round(safeValue * factor) / factor;
};

export const getCalculatedPricePrecision = (
  precisionLeft: number,
  precisionRight: number
): number => {
  return precisionLeft + precisionRight + NUMERIC_CONFIG.precision.extra;
};

export const applyCalculatedPricePrecision = (
  value: number,
  precisionLeft: number,
  precisionRight: number
): number => {
  const precision = getCalculatedPricePrecision(precisionLeft, precisionRight);

  return applyPricePrecision(value, precision);
};

// Common

export const formatAmount = (value: number, precision: number): string =>
  formatReceivedAmount(applyAmountPrecision(value, precision, 'credit'));
